import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi } from "ag-grid-community";
import { Checkbox } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { toast } from "react-toastify";

import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../components/utils";
import { determineUserAccessToResource } from "../../utils/auth";
import { usePermissionStore } from "../../store";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import {
  useGetAllBlackScholes,
  useGetBlackScholesReportDetails,
  useApproveBlackScholesReport,
  useDeleteBlackScholesReport,
} from "../../queries/blackScholes";
import {
  BlackScholesReport,
  EXPECTED_LIFE_CALCULATION_METHOD_LABELS,
} from "../../types/blackScholes";
import { formatDisplayDate } from "../../utils/date";
import { CTADropdown } from "../../components/shared/Dropdown";
import { CollapsibleComponent } from "../../components/shared/Collapsible";
import SearchInput from "../../components/shared/SearchInput";
import Loader from "../../shared/Loader";
import AlertDialog from "../../components/shared/AlertDialog";
import GenericFilterBar, {
  GenericFilterType,
} from "../companyProfile/utils/Filters";
import { NameAndId } from "../../types/Transaction";
import Tooltip from "../../components/shared/Tooltip";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";

const ReportRow = ({ report }: { report: BlackScholesReport }) => {
  const navigate = useNavigate();
  const currencyType = getCurrencyType();
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: details, isLoading } = useGetBlackScholesReportDetails(
    isExpanded ? report.id : undefined
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { mutate: approveReport, isLoading: isApproving } =
    useApproveBlackScholesReport();
  const { mutate: deleteReport, isLoading: isDeleting } =
    useDeleteBlackScholesReport();

  const rowData = useMemo(() => {
    if (!details?.calculatedData) return [];
    return Object.entries(details.calculatedData).map(([key, data]) => {
      let grantDate = key;
      let strikePrice;

      if (details.groupByGrantDateAndStrikePrice) {
        const parts = key.split("__");
        if (parts.length === 2) {
          grantDate = parts[0];
          strikePrice = Number(parts[1]);
        }
      }

      return {
        grantDate,
        strikePrice,
        ...data,
        ...data.values,
      };
    });
  }, [details?.calculatedData, details?.groupByGrantDateAndStrikePrice]);

  const columnDefs = useMemo<ColDef[]>(() => {
    const cols: ColDef[] = [
      {
        headerName: "Grant Date".toUpperCase(),
        field: "grantDate",
        pinned: "left",
        valueFormatter: (params: any) => formatDisplayDate(params.value),
        filter: "agDateColumnFilter",
        menuTabs: ["filterMenuTab"],
        sortable: true,
        resizable: true,
        getQuickFilterText: (params: any) => formatDisplayDate(params.value),
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (dateFromFilter.getTime() === new Date(cellValue).getTime())
              return 0;
            return dateFromFilter.getTime() > new Date(cellValue).getTime()
              ? -1
              : 1;
          },
        },
      },
    ];

    if (details?.groupByGrantDateAndStrikePrice) {
      cols.push({
        headerName: "Strike Price".toUpperCase(),
        field: "strikePrice",
        pinned: "left",
        valueFormatter: (params: any) =>
          params.value ? formatCurrency(params.value, currencyType) : "-",
        sortable: true,
        resizable: true,
        filter: "agNumberColumnFilter",
        menuTabs: ["filterMenuTab"],
      });
    }

    cols.push(
      ...([
        {
          headerName: "Valuation Date".toUpperCase(),
          field: "valuationDate",
          valueFormatter: (params: any) =>
            params.value ? formatDisplayDate(params.value) : "-",
          sortable: true,
          resizable: true,
          filter: "agDateColumnFilter",
          menuTabs: ["filterMenuTab"],
          getQuickFilterText: (params: any) =>
            params.value ? formatDisplayDate(params.value) : "-",
          filterParams: {
            comparator: (dateFromFilter: Date, cellValue: any) => {
              if (dateFromFilter.getTime() === new Date(cellValue).getTime())
                return 0;
              return dateFromFilter.getTime() > new Date(cellValue).getTime()
                ? -1
                : 1;
            },
          },
        },
        {
          headerName: "FMV (Option Value)".toUpperCase(),
          field: "fmv",
          valueFormatter: (params: any) =>
            params.value ? formatCurrency(params.value, currencyType) : "-",
          sortable: true,
          resizable: true,
          filter: "agNumberColumnFilter",
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Stock Price".toUpperCase(),
          field: "stockPrice",
          valueFormatter: (params: any) =>
            params.value ? formatCurrency(params.value, currencyType) : "-",
          sortable: true,
          resizable: true,
          filter: "agNumberColumnFilter",
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Volatility".toUpperCase(),
          field: "volatility",
          valueFormatter: (params: any) =>
            params.value
              ? `${Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}%`
              : "-",
          sortable: true,
          resizable: true,
          filter: "agNumberColumnFilter",
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Risk-Free Rate".toUpperCase(),
          field: "riskFreeRate",
          valueFormatter: (params: any) =>
            params.value
              ? `${Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}%`
              : "-",
          sortable: true,
          resizable: true,
          filter: "agNumberColumnFilter",
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Dividend Yield".toUpperCase(),
          field: "dividendYield",
          valueFormatter: (params: any) =>
            params.value
              ? `${Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}%`
              : "-",
          sortable: true,
          resizable: true,
          filter: "agNumberColumnFilter",
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "WAVP".toUpperCase(),
          field: "wavp",
          valueFormatter: (params: any) =>
            params.value
              ? Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                })
              : "-",
          sortable: true,
          resizable: true,
          filter: "agNumberColumnFilter",
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Orig. Maturity (Yrs)".toUpperCase(),
          field: "originalTimeToMaturity",
          valueFormatter: (params: any) =>
            params.value
              ? Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "-",
          sortable: true,
          resizable: true,
          filter: "agNumberColumnFilter",
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "d1".toUpperCase(),
          field: "d1",
          valueFormatter: (params: any) =>
            params.value
              ? Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                })
              : "-",
          sortable: true,
          resizable: true,
          filter: "agNumberColumnFilter",
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "d2".toUpperCase(),
          field: "d2",
          valueFormatter: (params: any) =>
            params.value
              ? Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                })
              : "-",
          sortable: true,
          resizable: true,
          filter: "agNumberColumnFilter",
          menuTabs: ["filterMenuTab"],
        },
      ] as ColDef[])
    );

    return cols;
  }, [details?.groupByGrantDateAndStrikePrice, currencyType]);

  const handleAction = (action: string) => {
    if (action === "Edit") {
      navigate(`/options/edit-valuation/${report.id}`);
    }
  };
  const gridApi = useRef<GridApi | null>(null);

  const onGridReady = (params: any) => {
    gridApi.current = params;
  };

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  const getAppliedFiltersForRow = () => {
    const filters = report.metaData?.filters;
    if (!filters) return [];

    const filterItems: { label: string; value: string }[] = [];

    if (filters.planIds && filters.planIds.length > 0) {
      filterItems.push({
        label: "Plans",
        value: filters.planIds.map((id) => id.name).join(", "),
      });
    }
    if (filters.employmentType && filters.employmentType.length > 0) {
      filterItems.push({
        label: "Employment Types",
        value: filters.employmentType.join(", "),
      });
    }
    if (filters.employeeStatus && filters.employeeStatus.length > 0) {
      filterItems.push({
        label: "Employee Status",
        value: filters.employeeStatus.join(", "),
      });
    }
    if (filters.grantStates && filters.grantStates.length > 0) {
      filterItems.push({
        label: "Grant States",
        value: filters.grantStates.join(", "),
      });
    }
    if (filters.subsidiaryIds && filters.subsidiaryIds.length > 0) {
      filterItems.push({
        label: "Subsidiaries",
        value: filters.subsidiaryIds.map((sub) => sub.name).join(", "),
      });
    }

    return filterItems;
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  const appliedFilters = getAppliedFiltersForRow();

  return (
    <CollapsibleComponent
      isOpen={!isExpanded}
      onClick={() => setIsExpanded(!isExpanded)}
      label={
        <div className="grid grid-cols-12 gap-2 w-full items-center pr-4">
          <div className="col-span-3">
            <VStack className="gap-2">
              <span className="text-gray-500 text-xs">Grant Period</span>
              <span className="font-bold text-gray-800 text-sm">
                {report.startDate ? formatDisplayDate(report.startDate) : "-"} -{" "}
                {report.endDate ? formatDisplayDate(report.endDate) : "-"}
              </span>
            </VStack>
          </div>
          <div className="col-span-3">
            <VStack className="gap-2">
              <span className="text-gray-500 text-xs">
                Valuation Report Date
              </span>
              <span className="font-bold text-gray-800 text-sm">
                {report.valuationDate
                  ? formatDisplayDate(report.valuationDate)
                  : "N/A"}
              </span>
            </VStack>
          </div>
          <div className="col-span-2">
            <VStack className="gap-2">
              <span className="text-gray-500 text-xs">Options Expiry</span>
              <span className="font-bold text-gray-800 text-sm">
                {report.metaData?.optionsExpiryFromPlanMaturity
                  ? "Active employee plan policy"
                  : `${report.metaData?.optionsExpiryYears} Years`}
              </span>
            </VStack>
          </div>
          <div className="col-span-2">
            <VStack className="gap-2">
              <span className="text-gray-500 text-xs">Created</span>
              <span className="font-bold text-gray-800 text-sm">
                {formatDisplayDate(report.createdAt)}
              </span>
            </VStack>
          </div>
          <HStack
            className="col-span-2 flex justify-between items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <HStack
              className={`px-3 py-1 rounded-full text-xs font-medium border ${
                report.state === "COMPLETED"
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-yellow-50 text-yellow-700 border-yellow-200"
              }`}
            >
              {report.state || "Pending"}
            </HStack>
            <HStack className="justify-end pl-20">
              {report.state === "DRAFT" && (
                <CTADropdown
                  actions={[{ name: "Edit" }]}
                  onAction={(action) => handleAction(action.name)}
                />
              )}
            </HStack>
          </HStack>
          <div className="col-span-12 mt-2 flex flex-wrap gap-2">
            {appliedFilters.length === 0 ? (
              <span className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600 whitespace-nowrap">
                No Filters Applied
              </span>
            ) : (
              appliedFilters.map((filter, index) => {
                const fullText = `${filter.label}: ${filter.value}`;
                const displayText = truncateText(fullText, 30);
                const isTruncated = fullText.length > 30;

                return (
                  <div
                    key={index}
                    className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600 whitespace-nowrap"
                  >
                    {isTruncated ? (
                      <Tooltip text={fullText} className="w-max max-w-xs">
                        <span>{displayText}</span>
                      </Tooltip>
                    ) : (
                      <span>{displayText}</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      }
      labelDetail=""
      className="mb-4 bg-white"
    >
      <Box className="mt-4">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            Loading details...
          </div>
        ) : (
          <VStack className="gap-4">
            {/* Header Row - Stats & Search */}
            <HStack className="justify-between items-center w-full bg-gray-50 rounded-lg p-4">
              <HStack className="gap-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-md border border-gray-200">
                  <span className="text-gray-500 text-xs uppercase tracking-wide">
                    Expected Life Method
                  </span>
                  <span className="font-bold text-gray-900 text-sm">
                    {
                      EXPECTED_LIFE_CALCULATION_METHOD_LABELS[
                        details?.expectedLifeCalculationMethod ||
                          "AGGREGATED_VESTING"
                      ]
                    }
                  </span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-md border border-gray-200">
                  <span className="text-gray-500 text-xs uppercase tracking-wide">
                    Options
                  </span>
                  <span className="font-bold text-gray-900 text-lg">
                    {report.metaData?.totalOptions?.toLocaleString() || "-"}
                  </span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-md border border-gray-200">
                  <span className="text-gray-500 text-xs uppercase tracking-wide">
                    Grants
                  </span>
                  <span className="font-bold text-gray-900 text-lg">
                    {report.metaData?.grantsCount?.toLocaleString() || "-"}
                  </span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-md border border-gray-200">
                  <span className="text-gray-500 text-xs uppercase tracking-wide">
                    Employees
                  </span>
                  <span className="font-bold text-gray-900 text-lg">
                    {report.metaData?.totalEmployees?.toLocaleString() || "-"}
                  </span>
                </div>
              </HStack>
              <div className="w-56">
                <SearchInput
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search in table..."
                  value={searchQuery}
                />
              </div>
            </HStack>

            {/* Grid */}
            <Box
              style={{
                height: `${Math.max(
                  400,
                  (rowData.length >= 10 ? 10 : rowData.length + 3) * 60
                )}px`,
              }}
              className="w-full h-full max-h-full overflow-x-auto ag-theme-material rounded-lg border border-gray-200"
            >
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                onGridReady={onGridReady}
                suppressRowTransform={true}
                defaultColDef={defaultColDef}
                animateRows={true}
                suppressRowClickSelection
                suppressMenuHide={true}
                pagination={false}
                quickFilterText={searchQuery}
                rowClass="border-t border-dashed cursor-pointer hover:bg-slate-50"
                overlayNoRowsTemplate={
                  '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
                }
              />
            </Box>

            {/* Approval Section */}
            {report.state === "DRAFT" && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <HStack className="items-center gap-2">
                  <Checkbox
                    checked={isConfirmed}
                    onChange={(e) => setIsConfirmed(e.target.checked)}
                    size="small"
                  />
                  <span className="text-sm text-gray-600">
                    I confirm that I have reviewed the valuation details and
                    want to proceed with approval.
                  </span>
                </HStack>
                <HStack className="gap-3">
                  <ButtonSecondary
                    disabled={isDeleting || isApproving}
                    onClick={() => {
                      setDeleteDialogOpen(true);
                    }}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </ButtonSecondary>
                  <ButtonPrimary
                    disabled={!isConfirmed || isApproving || isDeleting}
                    onClick={() => {
                      approveReport(report.id, {
                        onSuccess: () => {
                          toast.success(
                            "Valuation report approved successfully"
                          );
                        },
                        onError: () => {
                          toast.error("Failed to approve valuation report");
                        },
                      });
                    }}
                  >
                    {isApproving ? "Approving..." : "Approve Valuation"}
                  </ButtonPrimary>
                </HStack>
              </div>
            )}
          </VStack>
        )}
      </Box>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="md"
      >
        <AlertDialog
          message="Are you sure you want to delete this valuation report?"
          primaryActionText="Delete"
          secondaryActionText="Cancel"
          onPrimaryAction={() => {
            setDeleteDialogOpen(false);
            deleteReport(report.id, {
              onSuccess: () => {
                toast.success("Valuation report deleted successfully");
              },
              onError: () => {
                toast.error("Failed to delete valuation report");
              },
            });
          }}
          onSecondaryAction={() => {
            setDeleteDialogOpen(false);
          }}
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        />
      </Dialog>
    </CollapsibleComponent>
  );
};

const BlackScholesTable = () => {
  const navigate = useNavigate();
  const { data: _blackScholesData } = useGetAllBlackScholes();

  const [data, setData] = useState<BlackScholesReport[]>([]);
  useEffect(() => {
    if (_blackScholesData) {
      setData(_blackScholesData);
    }
  }, [_blackScholesData]);

  const { permission } = usePermissionStore();

  return (
    <Box className="w-full bg-white rounded-lg shadow-sm border border-gray-200 p-6 min-h-[500px]">
      <HStack className="items-center justify-between mb-8">
        <VStack className="min-w-max">
          <p className="text-lg font-medium text-gray-dark">
            Valuation Details
          </p>
          <p className="text-xs font-medium text-gray-light">
            {data?.length} Valuation Reports
          </p>
        </VStack>
        <HStack className="max-h-[30px] gap-4 items-center">
          <ButtonPrimary
            onClick={() => {
              navigate("/options/create-valuation");
            }}
            className={`flex justify-end flex-reverse-row items-center
            ${
              !determineUserAccessToResource(
                permission?.aclList || [],
                Resource.Valuation,
                DefaultAction.Create
              ) && "opacity-50 cursor-not-allowed "
            }
            `}
            disabled={
              !determineUserAccessToResource(
                permission?.aclList || [],
                Resource.Valuation,
                DefaultAction.Create
              )
            }
          >
            Create New Valuation
          </ButtonPrimary>
        </HStack>
      </HStack>

      <VStack className="gap-4">
        {data.map((report) => (
          <ReportRow key={report.id} report={report} />
        ))}
      </VStack>
    </Box>
  );
};

export default BlackScholesTable;
